/**
 * dp-connector-band.js — Beobachter für das Konfigurator-Band (#dp-connector).
 *
 * Der DP-Connector-App-Block (Fremd-App, reference/flyeralarm/app-new.js) rendert
 * in .dp-product-main__app-slot. Dieses Modul ändert die App nicht, sondern legt
 * einen dünnen Mantel darum:
 *   1. Ladezustand: nach SLOW_MS ohne Formularfelder ein Skeleton, nach
 *      STALLED_MS eine Klartextkarte (Seite neu laden / Designer / Support).
 *      Der App-Loader (.loading-container, app-new.js:355-372, 4460) wird nur per
 *      CSS-Klasse am Slot ausgeblendet, nie entfernt.
 *   2. Bedienbarkeit: <label for> für select.attribute-select (die App liefert
 *      .attribute-label ohne for, app-new.js:1109-1110), aria-live-Spiegel für
 *      Toasts (.flyeralarm-toast, :314-318) und bekannte Inline-Fehlertexte.
 *   3. Text-Mapping Sie→Du (TEXT_MAP, nur exakte Strings, idempotent).
 *   4. Sperre: solange window.dpBridge.status.phase 'preselect' oder 'handoff'
 *      ist (dp-connector-bridge.js: status.phase), Selects/Buttons sperren.
 *      Versand-Radios und Datei-Input bleiben frei, weil die Brücke sie per
 *      click()/change bedient und disabled-Inputs keinen click() ausführen.
 *   5. CI-Feinschliff (2026-09, Tier 2). Der App-Block liefert vier namenlose
 *      Karten in einem 2×2-Raster, eine einzige Schriftgröße und Preise als
 *      „16.14 EUR". Ohne sein DOM anzufassen ergänzt dieses Modul deshalb:
 *      a) Kartentitel mit Schrittnummer (CARD_TITLES) — die App verwirft in der
 *         Dropdown-Ansicht ihren eigenen Stepper (app-new.js:1034-1049), der
 *         Ablauf verliert dadurch jede Reihenfolge; die Nummer gibt sie zurück.
 *      b) Geldbeträge deutsch (formatEuro) und Mengen als „Stück" (formatUnits).
 *         .quantity-cell bleibt zahlenführend — dp-connector-app.js:126 liest
 *         dort die Menge per parseQuantityCell heraus.
 *      c) Preisstaffel auf PRICE_ROWS_VISIBLE Zeilen gekürzt, Rest hinter einer
 *         Schaltfläche. 20 Zeilen erschlugen die Karte daneben.
 *      d) Doppelte Optionstexte („Datencheck: Basis-Datencheck" unter
 *         „Basis-Datencheck") ausgeblendet, 0,00-€-Zeilen markiert.
 *      e) Zusammenfassung über dem Abschluss-Button — nur aus dem DOM gelesen.
 *      Alles idempotent und Teil von sweep(): die App rendert ihre Karten bei
 *      jeder Auswahl neu, der MutationObserver zieht die Ergänzungen nach.
 *
 * Ohne Wrapper [data-dp-band] oder ohne App-Slot beendet sich das Modul still.
 * Reine Helfer sind exportiert und unter tests/bridge/dp-connector-band.test.mjs getestet.
 */

export const VERSION = '0.3.0';
export const SLOW_MS = 8000;
/**
 * Erst nach zwei Minuten „antwortet nicht“ (vorher 20 s): Die App lädt je Produktseite ~47 MB
 * unkomprimiertes Varianten-JSON (KI-59; am 2026-09-23 nachgemessen: 47,1 MB, kein gzip, kein
 * Cache-Control, bei jedem Aufruf neu). Im Büro-Netz sind die Felder nach rund 5 s da, bei 5 Mbit/s
 * braucht allein dieser Download über eine Minute. Nach 20 s zum Neuladen zu raten war deshalb
 * falsch: Neuladen startet genau diesen Download noch einmal.
 */
export const STALLED_MS = 120000;
export const POLL_MS = 500;
export const SUPPORT_MAIL = 'support@dp-digital.de';

export const TEXT = Object.freeze({
  slow: 'Der Konfigurator lädt die Druckoptionen – bei langsamer Verbindung dauert das einen Moment.',
  /** Lebende Zeile darunter, damit die Wartezeit nicht wie ein Stillstand aussieht. */
  slowSince: (s) => `Lädt seit ${s}`,
  stalled: `Der Konfigurator antwortet gerade nicht. Lade die Seite neu – oder schreib uns an ${SUPPORT_MAIL}, wir helfen dir.`,
  reload: 'Seite neu laden',
  designer: 'Zum Designer',
});

/** Sichtbare App-Texte (app-new.js, Zeile) → Markenton. Nur exakte Strings; Unbekanntes bleibt. */
export const TEXT_MAP = Object.freeze({
  'Bitte füllen Sie alle Pflichtfelder aus.': 'Bitte fülle alle Pflichtfelder aus.', // :884
  'Bitte geben Sie einen Bestellnamen ein.': 'Bitte gib einen Auftragsnamen ein.', // :3981
  'Bitte geben Sie sowohl Breite als auch Höhe ein.': 'Bitte gib sowohl Breite als auch Höhe ein.', // :3129, :3811
  'Bitte laden Sie zuerst Ihre Druckdaten hoch.': 'Bitte lade zuerst deine Druckdatei hoch.', // :4287, :4418
  'Bitte wählen Sie alle Produktoptionen aus, um die Preise zu sehen.': 'Bitte wähle alle Produktoptionen aus, um die Preise zu sehen.', // :1533
  'Keine passende Variante gefunden. Bitte überprüfen Sie Ihre Auswahl.': 'Keine passende Variante gefunden. Bitte überprüfe deine Auswahl.', // :1492
  'Fehler beim Laden der Upload-Informationen. Bitte versuchen Sie es später erneut.': 'Die Upload-Hinweise konnten nicht geladen werden. Bitte versuch es später noch einmal.', // :2012
  'Upload-Anforderungen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.': 'Die Upload-Anforderungen konnten nicht geladen werden. Bitte versuch es später noch einmal.', // :3091
  'Für dieses Produkt sind keine Druckdaten erforderlich. Sie können direkt mit der Bestellung fortfahren.': 'Für dieses Produkt sind keine Druckdaten erforderlich. Du kannst direkt mit der Bestellung fortfahren.', // :1808, :2808
  'Pflichtfeld: Der Auftragsname hilft, Ihre Bestellung zu organisieren und zu verfolgen.': 'Pflichtfeld: Der Auftragsname hilft dir, deine Bestellung wiederzufinden.', // :1732, :2669 (data-tooltip)
  'Datei erfolgreich hochgeladen': 'Druckdatei übernommen', // :1921
  // Fachjargon und Behördendeutsch im sichtbaren Formular
  'Bruttosatz': 'Preis brutto', // :2481, :2551 (Tabellenkopf der Preisstaffel)
  'Auftragsnamen vergeben': 'Auftragsname', // :1732, :2669 (Beschriftung eines Felds, kein Aufruf)
  'Bestellname / max. 60 Zeichen': 'Höchstens 60 Zeichen', // :1735, :2672
  'Upload-Anforderungen': 'Das muss deine Datei erfüllen',
  // Englische Reste der App, die im deutschen Shop sichtbar würden
  'Loading variants...': 'Lade Varianten …', // :2135
  'No pricing information available.': 'Keine Preisangaben verfügbar.',
  'Not available in your country.': 'In deinem Land nicht verfügbar.',
  'No valid product variant selected for pricing. Please go back and select a product option.': 'Für die Preisberechnung fehlt eine gültige Variante. Bitte wähle eine Produktoption.',
});

/** Texte, die als Fehler in die aria-live-Region gespiegelt werden (Markenton, nach dem Mapping). */
export const ERROR_TEXTS = Object.freeze([
  TEXT_MAP['Keine passende Variante gefunden. Bitte überprüfen Sie Ihre Auswahl.'],
  TEXT_MAP['Fehler beim Laden der Upload-Informationen. Bitte versuchen Sie es später erneut.'],
  TEXT_MAP['Upload-Anforderungen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.'],
  'Fehler beim Laden der Preisdaten.',
  'Fehler beim Laden der Versanddetails.',
  'Fehler beim Laden der Versandoptionen.',
  'Für dieses Produkt ist kein Daten-Upload verfügbar oder erlaubt.',
  'Kein Upload-Format gefunden',
]);

// ── Reine Helfer ────────────────────────────────────────────────────────────

/** Bildet einen Textknoten-Inhalt ab; Leerraum und Toast-Marker (⚠️/✓) bleiben erhalten. */
export function mapText(raw) {
  const s = String(raw ?? '');
  const m = /^(\s*)(⚠️\s+|✓\s+)?([\s\S]*?)(\s*)$/.exec(s);
  if (!m) return s;
  const hit = TEXT_MAP[m[3]];
  return hit == null ? s : `${m[1]}${m[2] ?? ''}${hit}${m[4]}`;
}

/** Kernaussage ohne Leerraum/Marker — für den Fehlerabgleich. */
export function coreText(raw) {
  return String(raw ?? '').replace(/^\s*(⚠️|✓)\s+/, '').replace(/\s+/g, ' ').trim();
}

export function isErrorText(raw) {
  return ERROR_TEXTS.includes(coreText(raw));
}

/** 42 000 ms → „0:42" (wie pendingProgressText im Upload-Mantel). */
export function formatSince(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** 'ready' | 'loading' | 'slow' | 'stalled' — ohne DOM, damit testbar. */
export function evaluateLoadState({ startedAt = 0, now = 0, hasFields = false, configError = false, slowMs = SLOW_MS, stalledMs = STALLED_MS } = {}) {
  if (hasFields) return 'ready';
  if (configError) return 'stalled';
  const elapsed = now - startedAt;
  if (elapsed >= stalledMs) return 'stalled';
  if (elapsed >= slowMs) return 'slow';
  return 'loading';
}

/** Die Brücke arbeitet gerade am Formular (dp-connector-bridge.js: status.phase). */
export function isBridgeBusy(status) {
  if (!status || typeof status !== 'object') return false;
  return status.phase === 'preselect' || status.phase === 'handoff';
}

/** Wie viele Staffelzeilen ohne Aufklappen sichtbar bleiben. */
export const PRICE_ROWS_VISIBLE = 5;

/**
 * Kartentitel in Lesereihenfolge. Die App benennt ihre Karten mit
 * .dropdown-label („Grundpreis", „Versandart", „Optionen",
 * „Bestell- und Daten-Upload"); die Dropdown-Karte ganz oben hat gar keinen
 * Titel. „Grundpreis" beschreibt außerdem die falsche Sache — die Karte zeigt
 * die Mengenstaffel, „Grundpreis" heißt in derselben App die Zeile in der
 * Optionskarte. Deshalb hier eigene, eindeutige Titel je Karte statt eines
 * globalen TEXT_MAP-Eintrags (der beide „Grundpreis" träfe).
 */
export const CARD_TITLES = Object.freeze([
  ['.attributes-section', 'Dein Kalender'],
  ['.pricing-card', 'Menge und Preis'],
  ['.shipping-selector-card', 'Versand'],
  ['.options-card', 'Optionen'],
  ['.file-upload-card', 'Druckdatei und Abschluss'],
]);

/**
 * „16.14 EUR" → „16,14 €". Idempotent (frisst das eigene Ergebnis wieder).
 * Alles, was nicht wie ein Betrag aussieht, bleibt unverändert.
 */
export function formatEuro(raw) {
  const s = String(raw ?? '');
  const m = /^\s*(\d[\d.,]*)[.,](\d{2})\s*(?:EUR|€)\s*$/.exec(s);
  if (!m) return s;
  const grouped = m[1].replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${grouped},${m[2]} €`;
}

/**
 * „10 Stk." → „10 Stück". Die Zahl bleibt vorn und unverändert, damit
 * parseQuantityCell (dp-connector-app.js:126) sie weiterhin findet.
 */
export function formatUnits(raw) {
  const s = String(raw ?? '');
  const m = /^\s*(\d[\d.]*)\s*Stk\.?\s*$/.exec(s);
  return m ? `${m[1]} Stück` : s;
}

/** Ist der Betrag null? Solche Zeilen werden dezent gesetzt statt betont. */
export function isFreePrice(raw) {
  return /^\s*0+[.,]0{2}\s*(?:EUR|€)\s*$/.test(String(raw ?? ''));
}

/**
 * Die App wiederholt unter jeder Option „<Gruppe>: <Option>" als Beschreibung
 * (app-new.js, renderDynamicOptions). Das trägt nichts und wird ausgeblendet —
 * aber nur bei exakter Übereinstimmung, echte Beschreibungen bleiben stehen.
 */
export function isRedundantOptionText(groupTitle, label, description) {
  const norm = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
  const d = norm(description);
  if (!d) return false;
  const l = norm(label);
  return d === l || d === `${norm(groupTitle)}: ${l}`;
}

/** „Gesamt: 16,14 € · 1 Stück · Standard" — leer, solange kein Preis dasteht. */
export function summaryText({ total, quantity, shipping } = {}) {
  const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
  const head = clean(total);
  if (!head) return '';
  const rest = [clean(quantity), clean(shipping)].filter(Boolean);
  return rest.length ? `Gesamt: ${head} · ${rest.join(' · ')}` : `Gesamt: ${head}`;
}

/** Beschriftung für ein Select: data-attribute, sonst der Zeilentext vor „Bitte wählen“. */
export function labelTextFor(attribute, rowText = '') {
  const attr = String(attribute ?? '').replace(/\s+/g, ' ').trim();
  if (attr) return attr;
  const row = String(rowText ?? '').replace(/\s+/g, ' ').trim();
  return row.split('Bitte wählen')[0].trim() || 'Auswahl';
}

// ── Laufzeit (nur im Browser) ───────────────────────────────────────────────
const SEL = {
  fields: 'select.attribute-select, #order-name, #file-upload-vertical',
  select: 'select.attribute-select',
  lockable: 'select.attribute-select, button, a.btn, a.add_to_cart',
  toast: '.flyeralarm-toast',
  designer: 'a.dp-product-main__connector-designer, a[data-dp-designer-href]',
};
const MAPPED_ATTRS = ['data-tooltip', 'title', 'placeholder', 'aria-label'];
const CLS = { slow: 'dp-connector-band--slow', stalled: 'dp-connector-band--stalled', busy: 'dp-connector-band--busy' };

/** Zellen, in denen Beträge stehen. .quantity-cell fehlt bewusst — dort steht die Menge. */
const MONEY_SEL = '.price-cell, .option-price, #total-price, .section-headerr .price';
const MORE_TEXT = { show: 'Alle Mengen anzeigen', hide: 'Weniger Mengen anzeigen' };

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function applyTextMap(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const mapped = mapText(node.nodeValue);
    if (mapped !== node.nodeValue) node.nodeValue = mapped;
  }
  const elements = root.nodeType === 1 ? [root, ...root.querySelectorAll('*')] : [];
  for (const element of elements) {
    for (const name of MAPPED_ATTRS) {
      const value = element.getAttribute(name);
      if (value == null) continue;
      const mapped = mapText(value);
      if (mapped !== value) element.setAttribute(name, mapped);
    }
  }
}

/** Setzt Text nur, wenn er sich ändert — sonst löst jeder Sweep den nächsten aus. */
function setText(node, value) {
  if (node && node.textContent !== value) node.textContent = value;
}

/**
 * Gibt jeder App-Karte einen Titel mit Schrittnummer. Die App rendert ihre
 * Karten bei jeder Auswahl neu, deshalb läuft das in jedem Sweep und muss
 * idempotent sein.
 */
function decorateCards(slot) {
  let step = 0;
  for (const [selector, title] of CARD_TITLES) {
    const card = slot.querySelector(selector);
    if (!card) continue;
    step += 1;
    let label = card.querySelector('.dropdown-label');
    if (!label) {
      // Die Dropdown-Karte bringt keinen Titel mit (app-new.js:1036-1038).
      label = el('label', 'dropdown-label dp-band-cardhead');
      card.prepend(label);
    }
    let number = label.querySelector('.dp-band-step');
    if (!number) {
      number = el('span', 'dp-band-step');
      number.setAttribute('aria-hidden', 'true'); // die Ziffer ist Orientierung, kein Inhalt
      label.prepend(number);
    }
    setText(number, String(step));
    let name = label.querySelector('.dp-band-cardtitle');
    if (!name) {
      name = el('span', 'dp-band-cardtitle');
      label.append(name);
    }
    setText(name, title);
    // Den ursprünglichen App-Text der Karte leeren, nicht den Knoten entfernen.
    for (const node of [...label.childNodes]) {
      if (node.nodeType === 3 && node.nodeValue.trim()) node.nodeValue = '';
    }
    if (!label.hasAttribute('for') && label.getAttribute('role') !== 'heading') {
      label.setAttribute('role', 'heading');
      label.setAttribute('aria-level', '3');
    }
  }
}

/** Beträge deutsch, Mengen als „Stück“. */
function applyMoney(slot) {
  slot.querySelectorAll(MONEY_SEL).forEach((node) => {
    if (node.children.length) return;
    setText(node, formatEuro(node.textContent));
  });
  slot.querySelectorAll('.quantity-cell').forEach((node) => {
    if (node.children.length) return;
    setText(node, formatUnits(node.textContent));
  });
}

function syncMore(button, table) {
  const collapsed = table.dataset.dpRows === 'collapsed';
  const count = table.querySelectorAll('tbody tr.pricing-row').length;
  setText(button, collapsed ? `${MORE_TEXT.show} (${count})` : MORE_TEXT.hide);
  button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}

/** Lange Preisstaffeln auf die ersten Zeilen kürzen; die gewählte bleibt immer sichtbar. */
function collapsePricing(slot) {
  const table = slot.querySelector('table.pricing-table');
  if (!table) return;
  const container = table.closest('.pricing-tables-container')?.parentElement ?? table.parentElement;
  if (!container) return;
  let more = container.querySelector('.dp-band-more');
  if (table.querySelectorAll('tbody tr.pricing-row').length <= PRICE_ROWS_VISIBLE) {
    if (table.dataset.dpRows) delete table.dataset.dpRows;
    if (more) more.hidden = true;
    return;
  }
  if (!table.id) table.id = 'dp-band-pricing-table';
  if (!table.dataset.dpRows) table.dataset.dpRows = 'collapsed';
  if (!more) {
    more = el('button', 'dp-band-more');
    more.type = 'button';
    more.setAttribute('aria-controls', table.id);
    more.addEventListener('click', () => {
      table.dataset.dpRows = table.dataset.dpRows === 'collapsed' ? 'expanded' : 'collapsed';
      syncMore(more, table);
    });
    container.appendChild(more);
  }
  more.hidden = false;
  syncMore(more, table);
}

/** Optionsliste entrümpeln: doppelte Beschreibungen weg, Nullbeträge dezent. */
function tidyOptions(slot) {
  slot.querySelectorAll('.option-price').forEach((node) => {
    node.classList.toggle('dp-band-price--free', isFreePrice(node.textContent));
  });
  slot.querySelectorAll('.option-section').forEach((section) => {
    const title = section.querySelector('.section-title')?.textContent ?? '';
    section.querySelectorAll('.option-row').forEach((row) => {
      const description = row.querySelector('.option-description');
      if (!description) return;
      const label = row.querySelector('.option-header label')?.textContent ?? '';
      description.classList.toggle('dp-band-hidden', isRedundantOptionText(title, label, description.textContent));
    });
  });
}

/** Zusammenfassung direkt über dem Abschluss-Button — reines Lesen aus dem App-DOM. */
function renderSummary(slot) {
  const actions = slot.querySelector('.upload-actions');
  const host = actions?.parentElement;
  if (!host) return;
  const checked = slot.querySelector('input[name="shipping-type"]:checked');
  const text = summaryText({
    total: slot.querySelector('#total-price')?.textContent ?? '',
    quantity: slot.querySelector('.pricing-row.selected .quantity-cell')?.textContent ?? '',
    shipping: checked
      ? (checked.closest('.option-header')?.querySelector('label')?.textContent ?? checked.value)
      : '',
  });
  let line = host.querySelector('.dp-band-summary');
  if (!text) {
    line?.remove();
    return;
  }
  if (!line) {
    line = el('p', 'dp-band-summary');
    host.insertBefore(line, actions);
  }
  setText(line, text);
}

function ensureLabels(slot) {
  slot.querySelectorAll(SEL.select).forEach((select, index) => {
    if (select.dataset.dpBandLabelled) return;
    if (!select.id) select.id = `dp-band-select-${index}`;
    const group = select.closest('.attribute-group') ?? select.parentElement;
    let label = group?.querySelector('label');
    if (label) {
      if (!label.htmlFor) label.htmlFor = select.id;
    } else {
      label = el('label', 'dp-connector-band__label', labelTextFor(select.dataset.attribute, group?.textContent));
      label.htmlFor = select.id;
      select.parentNode.insertBefore(label, select);
    }
    select.dataset.dpBandLabelled = '1';
  });
  // Der Löschknopf am Datei-Chip trägt nur ein SVG (app-new.js:1960) und hätte
  // ohne diesen Namen keinen für Screenreader.
  slot.querySelectorAll('.file-preview-box .delete-btn').forEach((button) => {
    if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Datei entfernen');
  });
  const orderName = slot.querySelector('#order-name');
  if (orderName && !orderName.dataset.dpBandLabelled) {
    const title = orderName.parentElement?.querySelector('.section-title');
    if (title) {
      if (!title.id) title.id = 'dp-band-order-name-title';
      orderName.setAttribute('aria-labelledby', title.id);
    } else if (!orderName.getAttribute('aria-label')) {
      orderName.setAttribute('aria-label', 'Auftragsname');
    }
    orderName.dataset.dpBandLabelled = '1';
  }
}

function createLiveRegion(band) {
  const live = el('div', 'dp-connector-band__live');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');
  band.appendChild(live);
  let last = '';
  return (message) => {
    const text = coreText(message);
    if (!text) return;
    if (text === last) live.textContent = '';
    last = text;
    setTimeout(() => { live.textContent = text; }, 30);
  };
}

function mirrorInlineErrors(slot, announce, seen) {
  const walker = document.createTreeWalker(slot, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (seen.has(node) || !isErrorText(node.nodeValue)) continue;
    seen.add(node);
    announce(node.nodeValue);
  }
}

function setLocked(slot, locked) {
  if (locked) {
    slot.querySelectorAll(SEL.lockable).forEach((node) => {
      if (node.dataset.dpBandLocked) return;
      if (node.tagName === 'A') {
        if (node.getAttribute('aria-disabled') === 'true') return;
        node.setAttribute('aria-disabled', 'true');
      } else {
        if (node.disabled) return;
        node.disabled = true;
      }
      node.dataset.dpBandLocked = '1';
    });
  } else {
    slot.querySelectorAll('[data-dp-band-locked]').forEach((node) => {
      if (node.tagName === 'A') node.removeAttribute('aria-disabled');
      else node.disabled = false;
      delete node.dataset.dpBandLocked;
    });
  }
  const current = slot.getAttribute('aria-busy') === 'true';
  if (current !== locked) {
    slot.setAttribute('aria-busy', locked ? 'true' : 'false');
    slot.classList.toggle(CLS.busy, locked);
  }
}

function renderState(box, state, band) {
  box.replaceChildren();
  box.hidden = state !== 'slow' && state !== 'stalled';
  if (box.hidden) return;
  box.setAttribute('role', 'status');
  if (state === 'slow') {
    const skeleton = el('div', 'dp-connector-band__skeleton');
    skeleton.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 3; i += 1) skeleton.appendChild(el('span', 'dp-connector-band__bar'));
    const since = el('p', 'dp-connector-band__since', TEXT.slowSince('0:00'));
    since.dataset.dpBandSince = '';
    box.append(skeleton, el('p', 'dp-connector-band__hint', TEXT.slow), since);
    return;
  }
  const [before, after] = TEXT.stalled.split(SUPPORT_MAIL);
  const text = el('p', 'dp-connector-band__text', before);
  const mail = el('a', 'dp-connector-band__mail', SUPPORT_MAIL);
  mail.href = `mailto:${SUPPORT_MAIL}`;
  text.append(mail, after ?? '');
  const actions = el('div', 'dp-connector-band__actions');
  const reload = el('button', 'dp-connector-band__reload', TEXT.reload);
  reload.type = 'button';
  reload.addEventListener('click', () => window.location.reload());
  actions.appendChild(reload);
  const designer = band.querySelector(SEL.designer);
  if (designer?.getAttribute('href')) {
    const link = el('a', 'dp-connector-band__designer', TEXT.designer);
    link.href = designer.getAttribute('href');
    actions.appendChild(link);
  }
  box.append(text, actions);
}

function boot() {
  const wrapper = document.querySelector('[data-dp-band]');
  if (!wrapper) return;
  const band = wrapper.closest('#dp-connector, .dp-product-main__connector') ?? document.querySelector('#dp-connector');
  const slot = band?.querySelector('.dp-product-main__app-slot');
  if (!band || !slot) return;
  const slowMs = Number(wrapper.dataset.dpBandSlowMs) || SLOW_MS;
  const stalledMs = Number(wrapper.dataset.dpBandStalledMs) || STALLED_MS;
  const box = el('div', 'dp-connector-band__state');
  box.hidden = true;
  slot.appendChild(box);
  const announce = createLiveRegion(band);
  const seen = new WeakSet();
  const startedAt = Date.now();
  let loadState = 'loading';
  let locked = false;
  let sweepTimer = 0;

  const sweep = () => {
    sweepTimer = 0;
    applyTextMap(slot);
    applyMoney(slot);
    decorateCards(slot);
    collapsePricing(slot);
    tidyOptions(slot);
    renderSummary(slot);
    ensureLabels(slot);
    mirrorInlineErrors(slot, announce, seen);
    if (locked) setLocked(slot, true);
  };
  const scheduleSweep = () => {
    if (!sweepTimer) sweepTimer = setTimeout(sweep, 50);
  };
  const tick = () => {
    // Eingeklapptes Band (dp-product-main.liquid, Body hidden): Skeleton/Fehlerkarte dort nicht
    // rendern oder ansagen – erst, wenn der Kunde das Band öffnet (Technik-Prüfung 2026-09-16, P2-17).
    const collapsed = document.getElementById('dp-connector-body')?.hidden === true;
    if (loadState !== 'ready' && !collapsed) {
      const next = evaluateLoadState({
        startedAt, now: Date.now(), slowMs, stalledMs,
        hasFields: Boolean(slot.querySelector(SEL.fields)),
        configError: Boolean(window.flyeralarmApp?.configError),
      });
      if (next !== loadState) {
        loadState = next;
        slot.classList.toggle(CLS.slow, next === 'slow');
        slot.classList.toggle(CLS.stalled, next === 'stalled');
        renderState(box, next, band);
        if (next === 'stalled') announce(TEXT.stalled);
        if (next === 'stalled' && !slot.querySelector('.dp-main')) return clearInterval(interval); // App-Block fehlt ganz
      }
      // Lebende Wartezeile im Zustand „lädt noch": ohne sie sieht eine Minute Warten wie Stillstand aus.
      if (next === 'slow') {
        const since = box.querySelector('[data-dp-band-since]');
        const text = TEXT.slowSince(formatSince(Date.now() - startedAt));
        if (since && since.textContent !== text) since.textContent = text;
      }
    }
    const busy = isBridgeBusy(window.dpBridge?.status);
    if (busy !== locked) {
      locked = busy;
      setLocked(slot, busy);
    }
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'childList' && [...record.addedNodes].some((n) => n === box || box.contains(n))) continue;
      return scheduleSweep();
    }
  });
  observer.observe(slot, { subtree: true, childList: true, characterData: true });
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node?.nodeType !== 1 || !node.matches?.(SEL.toast)) continue;
        applyTextMap(node);
        if (node.classList.contains('flyeralarm-toast-error') || isErrorText(node.textContent)) announce(node.textContent);
      }
    }
  }).observe(document.body, { childList: true });

  sweep();
  const interval = setInterval(tick, POLL_MS);
  tick();
  window.dpBand = { version: VERSION, state: () => ({ loadState, locked }) };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
